import React from 'react';
import { IonToolbar, IonButtons, IonButton, IonTitle } from '@ionic/react';
import { ActionGuard } from '@/components/ActionGuard';
import { ActionGuardContainer } from '@/components/ActionGuardContainer';
import { ACTIONS, RESOURCES } from '@/contexts/CasbinContext';

interface UpdateToolbarProps {
  topicId?: string;
  saving: boolean;
  topicTitle: string;
  endBeforeStart: boolean;
  /** Whether the draft differs from the saved topic; Update waits for one. */
  hasChanges: boolean;
  onCancel: () => void;
  onUpdate: () => void;
}

/**
 * Update-mode toolbar, laid out as Ionic's modal confirm: Cancel in the start
 * slot and the strong confirm in the end slot, both in the toolbar's own color
 * so each platform renders its native header buttons. Update stays disabled
 * until the draft differs from the saved topic.
 *
 * Update is permission-guarded via an enclosing
 * `ActionGuardContainer`/`ActionGuard` (with `disableMode="hidden"`).
 */
const UpdateToolbar: React.FC<UpdateToolbarProps> = ({
  topicId,
  saving,
  topicTitle,
  endBeforeStart,
  hasChanges,
  onCancel,
  onUpdate,
}) => (
  <IonToolbar>
    <IonButtons slot="start">
      <IonButton onClick={onCancel} disabled={saving} aria-label="Cancel">
        Cancel
      </IonButton>
    </IonButtons>
    <IonTitle className="ion-hide-accessible">Edit Topic</IonTitle>
    <IonButtons slot="end">
      <ActionGuardContainer>
        <ActionGuard
          disableMode="hidden"
          action={ACTIONS.UPDATE}
          resource={RESOURCES.TOPICS}
          resourceId={topicId}
          helpText={{
            singleVerification: 'Verify yourself to update this topic',
            updateProfile: 'Update your profile to update this topic',
            setupProfile: 'Set up your profile to update this topic',
          }}
          guardedElem={
            <IonButton
              strong={true}
              onClick={onUpdate}
              disabled={
                saving || !hasChanges || !topicTitle.trim() || endBeforeStart
              }
              aria-label="Update Topic"
            >
              {saving ? 'Updating...' : 'Update'}
            </IonButton>
          }
        />
      </ActionGuardContainer>
    </IonButtons>
  </IonToolbar>
);

export default UpdateToolbar;
